/**
 * Created by Jonas Wålekvist on 2018-05-31.
 */

import {
    ISymbolSizeConfig,
    ISymbolStateConfig,
    ISymbolStates,
    StateReelSymbol,
    SymbolStateTransition
} from "@nolimitcity/slot-game/bin/core/reelsymbol/StateReelSymbol";
import {HorribleHeroesGameConfig} from "../HorribleHeroesGameConfig";
import {TimelineSprite} from "@nolimitcity/slot-game/bin/core/animation/TimelineSprite";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {MathHelper} from "@nolimitcity/slot-game/bin/core/utils/MathHelper";
import {TimelineLite, TweenLite} from "gsap";
import {HorribleHeroesReelController} from "../reel/HorribleHeroesReelController";

export interface IHorribleHeroesSymbolStates extends ISymbolStates {
    noWin: ISymbolStateConfig;
    attention: ISymbolStateConfig;
    mayhem: ISymbolStateConfig;
}

export class HorribleHeroesReelSymbol extends StateReelSymbol {
    protected _symMask: PIXI.Graphics | null;
    protected _displayScale: number;
    protected _winFireAnimation: TimelineSprite;
    protected _highLightX: PIXI.Sprite;
    protected _highLightFire: PIXI.Sprite; //#482
    protected _bg: PIXI.Sprite;
    protected _symImage: PIXI.Sprite;
    private _winFireTween: TweenLite;

    constructor(symName: string, reelId: number, state?: ISymbolStateConfig, size?: ISymbolSizeConfig) {
        super(symName, reelId, state, size);
    }

    public get symHeight(): number {
        if (this._symHeight == null) {
            return HorribleHeroesGameConfig.instance.SYMBOL_HEIGHT;
        }
        return this._symHeight;
    }

    public set symHeight(value: number) {
        this._symHeight = value;
        this.updateFrame(value);
        this.updateBackground(value);
        this.resetSymMask();
        this.drawSymMask();
    }

    public createEmptySprite(texture: string, anchorY: number = 0.5, name?: string): PIXI.Sprite {
        const temp: PIXI.Sprite = new PIXI.Sprite();
        temp.position.set(0, 0);
        temp.anchor.set(0.5, 0.5);
        if (texture.indexOf("*") > -1 && texture.indexOf("X") == -1) {
            texture = this.symName.split('_')[0];
        } else if (this.symName.indexOf("X") !== -1 && texture.indexOf("*") > -1) { //X_2*1
            texture = this.symName.split('_')[0];
        }

        temp.texture = GameResources.getTextures(texture)[0];
        temp.name = name ? name : texture;
        if (temp.name)
            this.addChild(temp);
        return temp;
    }

    public updateFrame(symHeight: number): void {
    }

    public updateBackground(symHeight: number): void {
        let scale: number = MathHelper.roundToDecimals(symHeight / 147, 2);
        for (let stateName in this._stateAnimations) {
            const state = this._stateAnimations[stateName];
            if (state != null) {
                state.anchor.y = 0.5;
                this.fitToSize(state, symHeight);
            }
        }

        if (this._bg) {
            if (this.symName.indexOf("*") > -1) {
                scale = MathHelper.roundToDecimals(symHeight / this.symHeight, 2);
            }
            this._displayScale = scale;
            this._bg.scale.set(1, this._displayScale);
        }
    }

    public adjustSize(symbolH: number, symId: number, duration: number = 0.17): TimelineLite | void {
        return new TimelineLite();
    }

    public playWinFire(potentialHighlight?: boolean): void {
        if (!this._winFireAnimation) {
            this.addWinFire();
        }
        //#337
        TweenLite.killTweensOf(this._winFireAnimation);
        this.addChild(this._winFireAnimation);
        this._winFireAnimation.name = "_winFireAnimation";
        if (potentialHighlight) {
            if (HorribleHeroesGameConfig.instance.HIGHMSYMS.indexOf(this.symName) > -1) {
                this._winFireAnimation.scale.set(0.95, (this.symHeight / 148) - 0.05);
                if (this.symName == "M1") {
                    this._winFireAnimation.blendMode = PIXI.BLEND_MODES.NORMAL;
                } else {
                    this._winFireAnimation.blendMode = PIXI.BLEND_MODES.ADD;
                }
                this.framesOnTopLayer();
            } else {
                this._winFireAnimation.blendMode = PIXI.BLEND_MODES.ADD;
            }
        } else {
            this._winFireAnimation.blendMode = PIXI.BLEND_MODES.NORMAL;
        }

        this._winFireAnimation.alpha = 1;
        this._winFireAnimation.show();
        this._winFireAnimation.playLoop();
    }

    public partialWin(): void {
        if (!this._winFireAnimation) {
            this.addWinFire();
        }
        this.addChild(this._winFireAnimation);
        this._winFireAnimation.name = "_winFireAnimation";
        this._winFireAnimation.alpha = 0;
        this._winFireAnimation.show();
        this._winFireAnimation.playLoop();
        this._winFireAnimation.scale.set(0.95, (this.symHeight / 148) - 0.05);
        this._winFireAnimation.blendMode = PIXI.BLEND_MODES.ADD;
        this._winFireAnimation.tint = 0xffffff;
        this._winFireTween = TweenLite.to(this._winFireAnimation, 0.166, {alpha: 0.5});
        this.framesOnTopLayer();
    }

    public playHighLightWin(potentialHighlight?: boolean): void {
        if (potentialHighlight) {
            if (!this._highLightX) {
                this._highLightX = this.addHighlightWinAnim("highlightX");
                this._highLightFire = this.addHighlightWinAnim("winPotential");
            }
        }
        if (potentialHighlight) {
            this._highLightX.visible = true;
            this._highLightX.alpha = 0;
            this._highLightX.scale.set(0.7);
            this._highLightFire.alpha = 0;
            this._highLightFire.visible = true;
            this._highLightFire.scale.set(0.95, (this.symHeight / 148) - 0.05);
            this._highLightX.blendMode = PIXI.BLEND_MODES.ADD;
            this._highLightFire.blendMode = PIXI.BLEND_MODES.ADD;
            this._highLightFire.tint = 0xffffff;
            const tl = new TimelineLite();
            tl.add(() => {
                TweenLite.to(this._highLightX, 0.23, {alpha: 0.8});
                TweenLite.to(this._highLightFire, 0.166, {alpha: 1});
                TweenLite.to(this._highLightX.scale, 0.5, {x: 1.2, y: 1.2});
            }, 0);
            tl.add(() => {
                TweenLite.to(this._highLightFire, 0.166, {alpha: 0.8});
            }, 0.166);
            tl.add(() => {
                TweenLite.to(this._highLightX, 0.27, {alpha: 0});
            }, 0.23);
            tl.add(() => {
                TweenLite.to(this._highLightFire, 0.2, {alpha: 0});
            }, 0.3);

            this.framesOnTopLayer();
        }
    }

    public addWinFire(): void {
        const sprite: TimelineSprite = new TimelineSprite(GameResources.getTextures("winPotential"), 20);
        sprite.anchor.set(0.5);
        sprite.hide();
        sprite.tint = 0xe65c00;
        this._winFireAnimation = sprite;
        this._winFireAnimation.alpha = 1;
    }

    // #247
    public stopWinFireAnimation(): void {
        if (this._winFireAnimation) {
            this._winFireAnimation.hide();
            this._winFireAnimation.stopLoop();
        }
    }

    public stopPotentialWin(): void {
        if (!this._winFireAnimation) {
            return;
        }
        this._winFireAnimation.hide();
        this._winFireAnimation.stopLoop();
    }

    public stopHighlightWin(): void {
        if (!this._winFireAnimation) {
            return;
        }
        if (this._winFireTween) {
            this._winFireTween.pause();
            this._winFireTween.kill();
        }
        TweenLite.to(this._winFireAnimation, 0.17, {
            alpha: 0, onComplete: () => {
                // #247
                this._winFireAnimation.hide();
                this._winFireAnimation.stopLoop();
            }
        });
        if (!this._highLightX) {
            return;
        }
        this._highLightX.alpha = 0;
        if (!this._highLightFire) {
            return;
        }
        this._highLightFire.alpha = 0;
    }

    public onRemove(): void {
        if (this._highLightX) {
            this.removeChild(this._highLightX);
        }
        if (this._winFireAnimation) {
            // #247
            this._winFireAnimation.hide();
            this._winFireAnimation.stopLoop();

            this.removeChild(this._winFireAnimation);
        }
        if (this._bg) {
            this.removeChild(this._bg);
        }

        this._currentState = null;
        this._currentStateAnimation = null;

        // TODO: shall we reuse something for the performance?
        if (this._stateAnimations) {
            for (let key in this._stateAnimations) {
                if (this._stateAnimations[key]) {
                    this.removeChild(this._stateAnimations[key]!);
                    this._stateAnimations[key]!.destroy({children: true});
                }
            }
        }
        this._stateAnimations = {};
        super.onRemove();
    }

    public placeSymsBelowTrailingSymbol(trailingSym: string): void {
        //#395
        //to place trail of X above other spin Symbols
        const trailSym: PIXI.DisplayObject = this.parent.getChildByName(trailingSym);
        if (trailSym) {
            const index: number = this.parent.getChildIndex(trailSym);
            if (index > 0) {
                this.parent.addChildAt(this, index - 1);
            }
        }
    }

    protected initAnimations(): void {
        if (["S", "W"].indexOf(this.symName) > -1) {
            return;
        }
        if (!this._bg) {
            this._bg = this.createEmptySprite("L_bg");
            this._bg.anchor.set(0.5, 0.5);
            this.addChild(this._bg);
        }
        if (this.symName.indexOf("MX") > -1) {
            this._symImage = this.createEmptySprite(this.symName, 0.5, "MX");
        } else {
            this._symImage = this.createEmptySprite(this.symName);
        }

        this.addChild(this._symImage);

        this.addWinFire();
        this.symHeight = HorribleHeroesReelController.SYM_SIZE[4].height;
    }

    protected resetSymMask(): void {
        if (this._symMask) {
            this.removeChild(this._symMask!);
            this._symMask = null;
        }
    }

    protected addNewStateAnimation(timeline: TimelineLite, newState: SymbolStateTransition, stateAnimation: TimelineSprite, delay: number): void {
        const symsWithBG: string[] = HorribleHeroesGameConfig.instance.HIGHMSYMS.concat("X");
        //#395
        if (newState.state.keyword === HorribleHeroesGameConfig.instance.SYMBOL_STATES.spin.keyword ||
            newState.state.keyword === HorribleHeroesGameConfig.instance.SYMBOL_STATES.mayhem.keyword) {
            super.addNewStateAnimation(timeline, newState, stateAnimation, delay);
            timeline.add(() => {
                //#195
                if (symsWithBG.indexOf(this.symName) > -1) {
                    if (this._bg) {
                        TweenLite.to(this._bg, delay, {alpha: 0});
                    }
                } //highsyms
                //#249 to slowly change into blur state
                TweenLite.to(this._symImage, delay, {
                    alpha: 0, onComplete: () => {
                        //Type def does not suppot null, even though the doc says "set to null" to remove mask.
                        (<any>this.mask) = null;
                        if (this._symMask) {
                            this._symMask!.alpha = 0;
                            stateAnimation.y = 0;
                            stateAnimation.anchor.set(0.5);
                        }
                    }
                });
            }, delay);
        } else {
            if (this.symName.indexOf("X") > -1) {
                super.addNewStateAnimation(timeline, newState, stateAnimation, delay);
            }
            timeline.add(() => {
                (<any>this.mask) = this._symMask;
                if (this._symMask) {
                    this._symMask!.alpha = 1;
                }
                this._symImage.alpha = 1;
                if (symsWithBG.indexOf(this.symName) > -1) {  //highsyms
                    if (this._bg) this._bg.alpha = 1;
                }
            }, delay);
        }

        if (["S", "X", "W"].indexOf(this.symName) > -1) {
            return;
        }

        if (this._winFireAnimation) {
            this._winFireAnimation.hide();
            this._winFireAnimation.stopLoop();
        }

        if (newState.state.keyword === HorribleHeroesGameConfig.instance.SYMBOL_STATES.noWin.keyword) {
            this._bg.tint = 0x777777;
            this._symImage.tint = 0x777777;
        }

        if (newState.state.keyword === HorribleHeroesGameConfig.instance.SYMBOL_STATES.win.keyword) {
            this._bg.tint = 0xffffff;
            this._symImage.tint = 0xffffff;
            this._winFireAnimation.show();
            timeline.add(() => this.playWinFire(), delay);
        }
        if (newState.state.keyword === HorribleHeroesGameConfig.instance.SYMBOL_STATES.normal.keyword) {
            this._bg.tint = 0xffffff;
            this._symImage.tint = 0xffffff;
        }
        if (newState.state.keyword !== HorribleHeroesGameConfig.instance.SYMBOL_STATES.spin.keyword) {
            this.fitToSize(stateAnimation);
        }
    }

    protected fitToSize(stateAnimation: TimelineSprite, symHeight?: number): void {
        if (!symHeight) {
            symHeight = this._symHeight!
        }
        let scale: number = MathHelper.roundToDecimals(symHeight / 147, 2);
        if (this.symName.indexOf("*") > -1) { //for stack syms
            scale = MathHelper.roundToDecimals(symHeight / this.symHeight, 2);
            stateAnimation.y = -symHeight / 4;
            this._symImage.y = -symHeight / 4;
        } else {
            if (HorribleHeroesGameConfig.instance.HIGHMSYMS.indexOf(this.symName) > -1) { //highsyms
                scale = 1;
                stateAnimation.y = -(symHeight * 0.1);

                this._symImage.y = -(symHeight * 0.1);
            } else {
                scale && stateAnimation.scale.set(scale);
                this._symImage.y = 0; //royals
                scale && this._symImage.scale.set(scale);
                if (this._winFireAnimation) {
                    this._winFireAnimation.scale.set(0.95, (this.symHeight / 148) - 0.05);
                }
            }
        }
        stateAnimation.scale.set(scale, scale);
        this._symImage.scale.set(scale, scale);
    }

    protected framesOnTopLayer(): void {
    }

    protected drawSymMask(): void {
    }

    protected reset(remove: boolean): void {
        if (this._winFireAnimation) {
            this._winFireAnimation.hide();
            this._winFireAnimation.stopLoop();
        }

        this.resetSymMask();

        super.reset(remove);
    }

    private addHighlightWinAnim(resourceName: string): PIXI.Sprite {
        const sprite: PIXI.Sprite = new PIXI.Sprite(GameResources.getTextures(resourceName)[0]);
        sprite.anchor.set(0.5);
        sprite.visible = false;
        this.addChild(sprite);
        sprite.name = "_" + resourceName;
        return sprite;
    }
}
